export interface BlogPost {
  slug: string
  title: string
  excerpt: string
  content: string
  author: {
    name: string
    avatar: string
    role: string
  }
  date: string
  readTime: string
  category: string
  image: string
  tags: string[]
}

export const blogPosts: BlogPost[] = [
  {
    slug: "Resolución 2275 de 2023 del Ministerio de Salud y Protección Social de Colombia",
    title: "Resolución 2275 de 2023 del Ministerio de Salud y Protección Social de Colombia: Guía Completa 2025",
    excerpt:
      "Descubre todo lo que necesitas saber sobre la facturación electrónica en el sector salud colombiano y cómo implementarla correctamente.",
    content: `
1. ¿Qué es la Resolución 2275 de 2023?

El Ministerio de Salud y Protección Social expidió la Resolución 2275 de 2023 con el propósito de modernizar y unificar el manejo del Registro Individual de Prestación de Servicios de Salud (RIPS).
Esta norma convierte al RIPS en el soporte obligatorio de la factura electrónica de venta (FEV) dentro del sistema de salud colombiano, marcando un paso decisivo hacia la transformación digital del sector.

2. Objetivo principal

La resolución busca establecer un modelo único para recolectar, validar y reportar la información relacionada con los servicios y tecnologías en salud.
Con este cambio, cada factura electrónica estará respaldada por un RIPS validado, garantizando que los datos reportados sean coherentes, verificables y cumplan con los estándares nacionales.

3. A quién aplica

La norma abarca a prácticamente todos los actores que intervienen en la prestación y facturación de servicios de salud, entre ellos:

- Instituciones Prestadoras de Servicios de Salud (IPS).
- Profesionales independientes del sector.
- Empresas de transporte asistencial.
- Proveedores de tecnologías en salud.
- Entidades responsables del pago, como EPS, ARL, entidades territoriales y demás pagadores.

Incluso aquellas entidades que no sean directamente prestadores, pero que deban reportar información al RIPS, están incluidas dentro del alcance.

4. Principales cambios introducidos

4.1. Estandarización del RIPS
El registro adopta una nueva estructura de datos, más completa y precisa.
Los archivos se generan en formato JSON, lo que facilita su lectura e integración con otros sistemas informáticos.

4.2. Vinculación con la factura electrónica
El RIPS se convierte en el soporte oficial de la Factura Electrónica de Venta (FEV) en salud.
Cada factura deberá corresponderse exactamente con los datos reportados en el RIPS, fortaleciendo la trazabilidad y transparencia del proceso.

4.3. Validación y código CUV
Antes de ser radicada ante la entidad pagadora, la información del RIPS debe pasar por una validación electrónica.
Al aprobarse, el sistema genera un Código Único de Validación (CUV), que confirma que la factura y el RIPS coinciden correctamente.

4.4. Plazos de radicación
Una vez validada la factura electrónica por la DIAN, el prestador dispone de 22 días hábiles para radicarla junto con su RIPS ante la entidad responsable del pago.

4.5. Seguridad y protección de datos
La resolución exige a los actores garantizar la confidencialidad, integridad y disponibilidad de la información médica y administrativa contenida en los registros.

5. Implementación y fechas clave

Aunque la resolución entró en vigor a finales de 2023, su implementación ha sido progresiva.
Durante 2024 se establecieron fases de prueba y adaptación tecnológica, y la obligatoriedad plena del nuevo esquema se fijó para el 1 de octubre de 2024.

Posteriormente, la Resolución 1884 de 2024 ajustó algunos plazos y lineamientos, con el fin de dar mayor flexibilidad a las instituciones que aún están adecuando sus sistemas.
    `,
    author: {
      name: "Dr. Carlos Mendoza",
      avatar: "/professional-doctor-headshot.png",
      role: "Especialista en Facturación de Salud",
    },
    date: "2025-10-22",
    readTime: "5 min",
    category: "Facturación",
    image: "/blog/facturacion-electronica-salud.jpg",
    tags: ["Facturación Electrónica", "DIAN", "Normatividad"],
  },
  {
    slug: "rips-validacion-errores-comunes",
    title: "RIPS: Validación y Errores Comunes que Debes Evitar",
    excerpt:
      "Aprende a validar correctamente los archivos RIPS y evita los errores más frecuentes que retrasan tus procesos de facturación.",
    content: `
# RIPS: Validación y Errores Comunes que Debes Evitar

Los Registros Individuales de Prestación de Servicios de Salud (RIPS) son fundamentales para la facturación en el sector salud. Conocer los errores comunes te ayudará a optimizar tus procesos.

## ¿Qué son los RIPS?

Los RIPS son archivos que contienen información detallada sobre los servicios de salud prestados a los usuarios del sistema.

## Errores Más Comunes

### 1. Datos Incompletos
Uno de los errores más frecuentes es enviar archivos con campos obligatorios vacíos.

### 2. Formato Incorrecto
Los archivos deben cumplir con el formato establecido por el Ministerio de Salud.

### 3. Inconsistencias en Fechas
Las fechas de atención deben ser coherentes y estar dentro del período de facturación.

## Cómo Validar Correctamente

Rapyfact incluye un validador automático de RIPS que detecta errores antes de enviar los archivos, ahorrándote tiempo y evitando rechazos.
    `,
    author: {
      name: "Dra. María González",
      avatar: "/professional-woman-headshot.png",
      role: "Auditora de Cuentas Médicas",
    },
    date: "2024-01-10",
    readTime: "4 min",
    category: "RIPS",
    image: "/blog/rips-validacion.jpg",
    tags: ["RIPS", "Validación", "Errores"],
  },
  {
    slug: "optimiza-gestion-ips-tecnologia",
    title: "Cómo Optimizar la Gestión de tu IPS con Tecnología",
    excerpt:
      "Descubre las mejores prácticas y herramientas tecnológicas para mejorar la eficiencia operativa de tu institución prestadora de servicios de salud.",
    content: `
# Cómo Optimizar la Gestión de tu IPS con Tecnología

La transformación digital es clave para mejorar la eficiencia y calidad de atención en las IPS. Te mostramos cómo lograrlo.

## Áreas Clave de Optimización

### Facturación y Cobro
Automatiza tus procesos de facturación para reducir errores y acelerar los pagos.

### Gestión de Citas
Implementa sistemas de agendamiento en línea para mejorar la experiencia del paciente.

### Historias Clínicas Digitales
Digitaliza las historias clínicas para un acceso rápido y seguro a la información.

## Beneficios de la Digitalización

- Reducción de costos operativos
- Mejora en la atención al paciente
- Mayor control y trazabilidad
- Cumplimiento normativo simplificado

## Implementación Exitosa

Rapyfact ofrece una suite completa de herramientas diseñadas específicamente para IPS, con implementación rápida y soporte continuo.
    `,
    author: {
      name: "Ing. Roberto Silva",
      avatar: "/professional-man-headshot.png",
      role: "Consultor en Tecnología de Salud",
    },
    date: "2024-01-05",
    readTime: "6 min",
    category: "Gestión",
    image: "/blog/gestion-ips-tecnologia.jpg",
    tags: ["IPS", "Tecnología", "Gestión"],
  },
  {
    slug: "seguridad-datos-sector-salud",
    title: "Seguridad de Datos en el Sector Salud: Mejores Prácticas",
    excerpt:
      "La protección de datos de pacientes es fundamental. Conoce las mejores prácticas para garantizar la seguridad de la información en tu institución.",
    content: `
# Seguridad de Datos en el Sector Salud: Mejores Prácticas

La seguridad de los datos de pacientes es una prioridad absoluta en el sector salud. Te explicamos cómo proteger la información sensible.

## Normatividad Vigente

En Colombia, la Ley 1581 de 2012 regula la protección de datos personales, incluyendo información médica.

## Medidas de Seguridad Esenciales

### Encriptación
Todos los datos deben estar encriptados tanto en tránsito como en reposo.

### Control de Acceso
Implementa sistemas de autenticación robustos y permisos granulares.

### Respaldos Regulares
Realiza copias de seguridad frecuentes y prueba su restauración.

### Auditorías
Mantén registros de acceso y realiza auditorías periódicas.

## Cumplimiento Normativo

Rapyfact cumple con todas las normativas de seguridad y protección de datos, almacenando información en la nube con los más altos estándares de seguridad.
    `,
    author: {
      name: "Dr. Carlos Mendoza",
      avatar: "/professional-doctor-headshot.png",
      role: "Especialista en Facturación de Salud",
    },
    date: "2023-12-28",
    readTime: "5 min",
    category: "Seguridad",
    image: "/blog/seguridad-datos-salud.jpg",
    tags: ["Seguridad", "Datos", "Normatividad"],
  },
  {
    slug: "tendencias-tecnologia-salud-2024",
    title: "Tendencias en Tecnología para el Sector Salud en 2024",
    excerpt:
      "Explora las principales tendencias tecnológicas que están transformando el sector salud y cómo pueden beneficiar a tu institución.",
    content: `
# Tendencias en Tecnología para el Sector Salud en 2024

El sector salud está experimentando una transformación digital acelerada. Conoce las tendencias que marcarán el 2024.

## Inteligencia Artificial

La IA está revolucionando el diagnóstico, la gestión de datos y la atención al paciente.

## Telemedicina

La atención médica remota se consolida como una opción permanente y eficiente.

## Blockchain

Esta tecnología promete mejorar la seguridad y trazabilidad de los datos médicos.

## Internet de las Cosas Médicas (IoMT)

Dispositivos conectados que permiten monitoreo continuo de pacientes.

## Automatización de Procesos

La automatización de tareas administrativas libera tiempo para la atención al paciente.

## Preparándote para el Futuro

Rapyfact se mantiene a la vanguardia tecnológica, incorporando las últimas innovaciones para ofrecer el mejor servicio a nuestros clientes.
    `,
    author: {
      name: "Dra. María González",
      avatar: "/professional-woman-headshot.png",
      role: "Auditora de Cuentas Médicas",
    },
    date: "2023-12-20",
    readTime: "7 min",
    category: "Tecnología",
    image: "/blog/tendencias-tecnologia-2024.jpg",
    tags: ["Tendencias", "Tecnología", "Innovación"],
  },
  {
    slug: "mejora-flujo-caja-ips",
    title: "Estrategias para Mejorar el Flujo de Caja en tu IPS",
    excerpt:
      "Aprende técnicas efectivas para optimizar el flujo de caja de tu institución de salud y garantizar su sostenibilidad financiera.",
    content: `
# Estrategias para Mejorar el Flujo de Caja en tu IPS

El flujo de caja es vital para la sostenibilidad de cualquier IPS. Te compartimos estrategias probadas para optimizarlo.

## Desafíos Comunes

- Retrasos en pagos de EPS
- Glosas y devoluciones
- Procesos de facturación lentos
- Falta de visibilidad financiera

## Estrategias de Mejora

### 1. Facturación Oportuna
Factura inmediatamente después de prestar el servicio para acelerar los pagos.

### 2. Reducción de Glosas
Implementa controles de calidad para minimizar errores que generen glosas.

### 3. Seguimiento Activo
Monitorea constantemente el estado de tus cuentas por cobrar.

### 4. Automatización
Automatiza procesos para reducir tiempos y errores.

## Herramientas de Apoyo

Rapyfact ofrece reportes financieros en tiempo real y alertas automáticas para ayudarte a mantener un flujo de caja saludable.
    `,
    author: {
      name: "Ing. Roberto Silva",
      avatar: "/professional-man-headshot.png",
      role: "Consultor en Tecnología de Salud",
    },
    date: "2023-12-15",
    readTime: "5 min",
    category: "Finanzas",
    image: "/blog/flujo-caja-ips.jpg",
    tags: ["Finanzas", "Flujo de Caja", "Gestión"],
  },
]

export const categories = ["Todos", "Facturación", "RIPS", "Gestión", "Seguridad", "Tecnología", "Finanzas"]

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug)
}

export function getBlogPostsByCategory(category: string): BlogPost[] {
  if (category === "Todos") return blogPosts
  return blogPosts.filter((post) => post.category === category)
}
